import { calculateMeasuredResult } from "../challenge/measured-scoring.js";
import type {
  CourseHoleSnapshot,
  NassauResult,
  NassauSegmentResult,
  RoundParticipantSnapshot,
  RoundResult,
  RoundSnapshot,
  RoundStanding,
  TeamId,
} from "./round-types.js";

import { RoundResultError } from "./round-errors.js";
import { allocateHandicapStrokes } from "./round-handicap.js";
import { isRoundV3 } from "./round-types.js";
import { calculateV3RoundResult } from "../contest/contest-result.js";
export { RoundResultError, allocateHandicapStrokes };

export function calculateRoundResult(snapshot: RoundSnapshot): RoundResult {
  if (snapshot.rules.measuredChallenge)
    return calculateMeasuredResult(snapshot);
  if (isRoundV3(snapshot)) return calculateV3RoundResult(snapshot);
  switch (snapshot.rules.format) {
    case "stroke-play":
      return scoreByTotal(snapshot);
    case "stableford":
      return scoreStableford(snapshot);
    case "match-play":
      return scoreMatch(snapshot);
    case "nassau":
      return scoreNassau(snapshot);
    case "team-best-ball":
      return scoreTeamBestBall(snapshot, false);
    case "team-double-duel":
      return scoreTeamBestBall(snapshot, true);
    case "team-alternate-shot":
    case "team-scramble":
    case "team-greensomes":
      return scoreTeamTotals(snapshot);
    case "high-low":
      return scoreMetric(snapshot);
    case "declared-winner":
      return scoreDeclared(snapshot);
  }
}

function scoreByTotal(snapshot: RoundSnapshot): RoundResult {
  requireAllScores(
    snapshot,
    snapshot.participants.map((item) => item.id),
  );
  const standings = snapshot.participants.map((participant) => {
    const gross = grossTotal(snapshot, participant.id);
    return standing(participant.id, {
      gross,
      net: netTotal(snapshot, participant),
      stableford: null,
      holesWon: null,
      metric: null,
    });
  });
  const value = (item: RoundStanding) =>
    snapshot.rules.handicapMode === "off" ? item.gross! : item.net!;
  standings.sort((left, right) => value(left) - value(right));
  return result(snapshot, standings, winnersBy(standings, value, "min"));
}

function scoreStableford(snapshot: RoundSnapshot): RoundResult {
  requireAllScores(
    snapshot,
    snapshot.participants.map((item) => item.id),
  );
  const standings = snapshot.participants.map((participant) =>
    standing(participant.id, {
      gross: grossTotal(snapshot, participant.id),
      net: netTotal(snapshot, participant),
      stableford: stablefordTotal(snapshot, participant),
      holesWon: null,
      metric: null,
    }),
  );
  standings.sort((left, right) => right.stableford! - left.stableford!);
  return result(
    snapshot,
    standings,
    winnersBy(standings, (item) => item.stableford!, "max"),
  );
}

function scoreMatch(snapshot: RoundSnapshot): RoundResult {
  requireAllScores(
    snapshot,
    snapshot.participants.map((item) => item.id),
  );
  const wins = matchWins(snapshot, snapshot.course.holes);
  const standings = snapshot.participants
    .map((participant) =>
      standing(participant.id, {
        gross: grossTotal(snapshot, participant.id),
        net: netTotal(snapshot, participant),
        stableford: null,
        holesWon: wins[participant.id] ?? 0,
        metric: null,
      }),
    )
    .sort((left, right) => right.holesWon! - left.holesWon!);
  return result(
    snapshot,
    standings,
    winnersBy(standings, (item) => item.holesWon!, "max"),
  );
}

function scoreNassau(snapshot: RoundSnapshot): RoundResult {
  if (
    snapshot.participants.length !== 2 ||
    snapshot.course.holes.length !== 18
  ) {
    throw new RoundResultError("Nassau needs two players and eighteen holes.");
  }
  requireAllScores(
    snapshot,
    snapshot.participants.map((item) => item.id),
  );
  const front = segment(snapshot, snapshot.course.holes.slice(0, 9));
  const back = segment(snapshot, snapshot.course.holes.slice(9, 18));
  const overall = segment(snapshot, snapshot.course.holes);
  const nassau: NassauResult = { front, back, overall };
  const segmentWins = Object.fromEntries(
    snapshot.participants.map((participant) => [participant.id, 0]),
  ) as Record<string, number>;
  for (const current of [front, back, overall]) {
    if (!current.tied) {
      const winnerId = current.winnerIds[0]!;
      segmentWins[winnerId] = (segmentWins[winnerId] ?? 0) + 1;
    }
  }
  const standings = snapshot.participants
    .map((participant) =>
      standing(participant.id, {
        gross: grossTotal(snapshot, participant.id),
        net: netTotal(snapshot, participant),
        stableford: null,
        holesWon: segmentWins[participant.id]!,
        metric: null,
      }),
    )
    .sort((left, right) => right.holesWon! - left.holesWon!);
  return {
    ...result(
      snapshot,
      standings,
      winnersBy(standings, (item) => item.holesWon!, "max"),
    ),
    nassau,
  };
}

function scoreTeamBestBall(
  snapshot: RoundSnapshot,
  includeWorst: boolean,
): RoundResult {
  const targetIds = snapshot.participants.map((item) => item.id);
  requireAllScores(snapshot, targetIds);
  const totals = { a: 0, b: 0 };
  const points = { a: 0, b: 0 };
  for (const hole of snapshot.course.holes) {
    const teamA = teamHoleValues(snapshot, "a", hole);
    const teamB = teamHoleValues(snapshot, "b", hole);
    totals.a += Math.min(...teamA) + (includeWorst ? Math.max(...teamA) : 0);
    totals.b += Math.min(...teamB) + (includeWorst ? Math.max(...teamB) : 0);
    awardLower(points, Math.min(...teamA), Math.min(...teamB));
    if (includeWorst)
      awardLower(points, Math.max(...teamA), Math.max(...teamB));
  }
  const standings = teamStandings(totals, points);
  return result(
    snapshot,
    standings,
    winnersBy(standings, (item) => item.holesWon!, "max"),
  );
}

function scoreTeamTotals(snapshot: RoundSnapshot): RoundResult {
  const targetIds = ["team:a", "team:b"];
  requireAllScores(snapshot, targetIds);
  const totals = {
    a: grossTotal(snapshot, "team:a"),
    b: grossTotal(snapshot, "team:b"),
  };
  const points = { a: 0, b: 0 };
  for (const hole of snapshot.course.holes) {
    awardLower(
      points,
      score(snapshot, "team:a", hole),
      score(snapshot, "team:b", hole),
    );
  }
  const standings = teamStandings(totals, points);
  return result(
    snapshot,
    standings,
    winnersBy(standings, (item) => item.holesWon!, "max"),
  );
}

function teamHoleValues(
  snapshot: RoundSnapshot,
  teamId: TeamId,
  hole: CourseHoleSnapshot,
): number[] {
  return teamMembers(snapshot, teamId).map((member) =>
    score(snapshot, member.id, hole),
  );
}

function awardLower(
  points: Record<TeamId, number>,
  teamAValue: number,
  teamBValue: number,
): void {
  if (teamAValue < teamBValue) points.a += 1;
  if (teamBValue < teamAValue) points.b += 1;
}

function teamStandings(
  totals: Record<TeamId, number>,
  points: Record<TeamId, number>,
): RoundStanding[] {
  return (["a", "b"] as TeamId[])
    .map((teamId) =>
      standing("team:" + teamId, {
        gross: totals[teamId],
        net: totals[teamId],
        stableford: null,
        holesWon: points[teamId],
        metric: null,
      }),
    )
    .sort((left, right) => right.holesWon! - left.holesWon!);
}

function scoreMetric(snapshot: RoundSnapshot): RoundResult {
  const rule = snapshot.rules.metric;
  if (!rule) throw new RoundResultError("The measured result rule is missing.");
  const standings = snapshot.participants.map((participant) => {
    const metric = snapshot.metrics[participant.id];
    if (metric === null || metric === undefined || !Number.isFinite(metric)) {
      throw new RoundResultError("A required metric is missing.");
    }
    return standing(participant.id, {
      gross: null,
      net: null,
      stableford: null,
      holesWon: null,
      metric,
    });
  });
  standings.sort((left, right) =>
    rule.lowerWins
      ? left.metric! - right.metric!
      : right.metric! - left.metric!,
  );
  return result(
    snapshot,
    standings,
    winnersBy(
      standings,
      (item) => item.metric!,
      rule.lowerWins ? "min" : "max",
    ),
  );
}

function scoreDeclared(snapshot: RoundSnapshot): RoundResult {
  if (snapshot.declaredWinnerIds.length < 1) {
    throw new RoundResultError("A declared winner is missing.");
  }
  const allowed = new Set(snapshot.participants.map((item) => item.id));
  if (snapshot.declaredWinnerIds.some((id) => !allowed.has(id))) {
    throw new RoundResultError("A declared winner is not in the round.");
  }
  const standings = snapshot.participants.map((participant) =>
    standing(participant.id, {
      gross: null,
      net: null,
      stableford: null,
      holesWon: null,
      metric: null,
    }),
  );
  return result(snapshot, standings, snapshot.declaredWinnerIds);
}

function segment(
  snapshot: RoundSnapshot,
  holes: CourseHoleSnapshot[],
): NassauSegmentResult {
  const wins = matchWins(snapshot, holes);
  const highest = Math.max(...Object.values(wins));
  const winnerIds = snapshot.participants
    .map((item) => item.id)
    .filter((id) => wins[id] === highest);
  return { winnerIds, tied: winnerIds.length > 1 };
}

function matchWins(
  snapshot: RoundSnapshot,
  holes: CourseHoleSnapshot[],
): Record<string, number> {
  const wins = Object.fromEntries(
    snapshot.participants.map((participant) => [participant.id, 0]),
  ) as Record<string, number>;
  for (const hole of holes) {
    const values = snapshot.participants.map((participant) => ({
      id: participant.id,
      value:
        score(snapshot, participant.id, hole) -
        strokesOn(snapshot, participant, hole),
    }));
    const lowest = Math.min(...values.map((item) => item.value));
    const leaders = values.filter((item) => item.value === lowest);
    if (leaders.length === 1) {
      const leaderId = leaders[0]!.id;
      wins[leaderId] = (wins[leaderId] ?? 0) + 1;
    }
  }
  return wins;
}

function stablefordTotal(
  snapshot: RoundSnapshot,
  participant: RoundParticipantSnapshot,
): number {
  return snapshot.course.holes.reduce((total, hole) => {
    const net =
      score(snapshot, participant.id, hole) -
      strokesOn(snapshot, participant, hole);
    return total + Math.max(0, 2 + hole.par - net);
  }, 0);
}

function netTotal(
  snapshot: RoundSnapshot,
  participant: RoundParticipantSnapshot,
): number {
  return snapshot.course.holes.reduce(
    (total, hole) =>
      total +
      score(snapshot, participant.id, hole) -
      strokesOn(snapshot, participant, hole),
    0,
  );
}

function strokesOn(
  snapshot: RoundSnapshot,
  participant: RoundParticipantSnapshot,
  hole: CourseHoleSnapshot,
): number {
  if (snapshot.rules.handicapMode === "off") return 0;
  const minimum =
    snapshot.rules.handicapMode === "relative"
      ? Math.min(
          ...snapshot.participants.map((item) => item.playingHandicap ?? 0),
        )
      : 0;
  const allocation = allocateHandicapStrokes(
    snapshot.course.holes,
    (participant.playingHandicap ?? 0) - minimum,
  );
  const index = snapshot.course.holes.findIndex(
    (item) => item.number === hole.number,
  );
  return allocation[index] ?? 0;
}

function grossTotal(snapshot: RoundSnapshot, targetId: string): number {
  return snapshot.course.holes.reduce(
    (total, hole) => total + score(snapshot, targetId, hole),
    0,
  );
}

function score(
  snapshot: RoundSnapshot,
  targetId: string,
  hole: CourseHoleSnapshot,
): number {
  const value = snapshot.scores[targetId]?.[hole.number];
  if (value === null || value === undefined) {
    throw new RoundResultError("A required score is missing.");
  }
  return value;
}

function requireAllScores(snapshot: RoundSnapshot, targetIds: string[]): void {
  for (const targetId of targetIds) {
    for (const hole of snapshot.course.holes) score(snapshot, targetId, hole);
  }
}

function teamMembers(
  snapshot: RoundSnapshot,
  teamId: TeamId,
): RoundParticipantSnapshot[] {
  const members = snapshot.participants.filter(
    (item) => item.teamId === teamId,
  );
  if (members.length !== 2) {
    throw new RoundResultError("Each team needs two players.");
  }
  return members;
}

function standing(
  targetId: string,
  values: Omit<RoundStanding, "targetId">,
): RoundStanding {
  return { targetId, ...values };
}

function winnersBy(
  standings: RoundStanding[],
  value: (standing: RoundStanding) => number,
  direction: "min" | "max",
): string[] {
  const best =
    direction === "min"
      ? Math.min(...standings.map(value))
      : Math.max(...standings.map(value));
  return standings
    .filter((item) => value(item) === best)
    .map((item) => item.targetId);
}

function result(
  snapshot: RoundSnapshot,
  standings: RoundStanding[],
  winnerIds: string[],
): RoundResult {
  return {
    calculationVersion: "2",
    format: snapshot.rules.format,
    winnerIds,
    tied: winnerIds.length > 1,
    standings,
    nassau: null,
  };
}
